import { useState } from "react";

import Pill, { PillTone } from "@buildpy/ui/pill";
import { Button } from "@buildpy/ui/button";
import { Label } from "@buildpy/ui/label";
import { Textarea } from "@buildpy/ui/textarea";
import {
  BandOption,
  fillFromPaste,
  PasteFill,
  PasteFillField,
  PasteFillStatus,
  PasteFormValues,
  phoneChipsOf,
} from "@buildpy/core/modules/customer/paste-fill";
import { PasteOwnInfo, parsePastedInquiry, pasteSkipText } from "@buildpy/core/modules/customer/paste-parse";

const FIELD_LABEL: Record<PasteFillField, string> = {
  name: "이름",
  phone: "휴대폰",
  site_address: "현장 주소",
  area_band: "평형대",
  memo: "메모",
};

// picked = 여러 번호 중 사람이 고름(화면에서만 생기는 상태).
type RowStatus = PasteFillStatus | "picked";

const STATUS_TEXT: Record<RowStatus, string> = {
  filled: "붙여넣기로 채움",
  picked: "후보에서 고름",
  kept: "이미 입력돼 있어 그대로 둠",
  ambiguous: "후보가 여러 개라 비워 둠",
  "no-band": "맞는 평형대가 없어 비워 둠",
  none: "찾지 못함",
};

const STATUS_TONE: Record<RowStatus, PillTone> = {
  filled: "primary",
  picked: "primary",
  kept: "neutral",
  ambiguous: "hold",
  "no-band": "hold",
  none: "neutral",
};

interface ResultProps {
  result: PasteFill;
  // 지금 휴대폰 칸의 값 — 후보 칩의 선택 표시와 노출을 이 값으로 정한다.
  phone: string;
  onChoosePhone: (phone: string) => void;
}

// 채우기 결과 목록 — 훅이 없어 테스트가 직접 불러 칩의 onClick 배선을 본다.
export const PasteResultList = ({ result, phone, onChoosePhone }: ResultProps) => {
  const now = phone.trim();
  const chips = phoneChipsOf(result, now);
  return (
    <ul className="flex flex-col gap-y-1.5 border-t border-border pt-2 text-sm" data-testid="paste-inquiry-result">
      {result.rows.map((row) => {
        const picked = row.field === "phone" && chips.includes(now) && now !== (result.patch.phone ?? "");
        const status: RowStatus = picked ? "picked" : row.status;
        const shown = picked ? now : row.shown;
        return (
          <li key={row.field} className="flex flex-wrap items-center gap-x-2 gap-y-1" data-status={status}>
            <span className="w-16 shrink-0 text-muted-foreground">{FIELD_LABEL[row.field]}</span>
            <Pill tone={STATUS_TONE[status]}>{STATUS_TEXT[status]}</Pill>
            {shown && <span className="min-w-0 break-all">{shown}</span>}
            {row.field === "phone" && chips.length > 0 && (
              <span className="flex flex-wrap gap-1" data-testid="paste-phone-choices">
                {chips.map((choice) => (
                  <Button
                    key={choice}
                    aria-pressed={now === choice}
                    size="sm"
                    type="button"
                    variant={now === choice ? "primary" : "outline"}
                    onClick={() => onChoosePhone(choice)}>
                    {choice}
                  </Button>
                ))}
              </span>
            )}
          </li>
        );
      })}
      {result.memoTruncated && <li className="text-xs text-muted-foreground">원문이 길어 메모에는 앞부분만 넣었습니다.</li>}
      {result.areaM2 !== undefined && (
        <li className="text-xs text-muted-foreground">
          {result.areaM2}㎡ 는 공급·전용을 몰라 평형대로 바꾸지 않았습니다 — 원문을 보고 골라 주세요.
        </li>
      )}
      {result.skipped?.map((skip) => (
        <li key={`${skip.kind}-${skip.value}`} className="text-xs text-muted-foreground" data-testid="paste-skipped">
          {skip.value} — {pasteSkipText(skip)}
        </li>
      ))}
    </ul>
  );
};

interface Props {
  areaBands: readonly BandOption[];
  phone: string;
  read: () => PasteFormValues;
  apply: (patch: Partial<PasteFormValues>) => void;
  // 우리 회사 번호·주소 — 서명을 고객 값으로 채우지 않게 뺀다.
  own?: PasteOwnInfo;
}

// 고객 등록 폼 위의 「문의 글 붙여 넣기」 — 빈 칸만 채우고 저장은 폼의 저장 버튼으로 사람이 한다.
const PasteInquiryPanel = ({ areaBands, phone, read, apply, own }: Props) => {
  const [text, setText] = useState("");
  const [result, setResult] = useState<PasteFill | null>(null);
  const [inputCut, setInputCut] = useState(false);

  const fill = () => {
    const parsed = parsePastedInquiry(text, own);
    const next = fillFromPaste(read(), parsed, areaBands);
    apply(next.patch);
    setResult(next);
    setInputCut(parsed.input_truncated === true);
  };

  return (
    <details className="rounded-md border border-border px-3 py-2" data-testid="paste-inquiry">
      <summary className="cursor-pointer text-sm font-semibold">문의 글 붙여 넣기</summary>
      <div className="mt-3 flex flex-col gap-y-2">
        <Label htmlFor="paste-inquiry-text">카톡·문자로 받은 문의를 그대로 붙여 넣으면 비어 있는 칸만 채웁니다</Label>
        <Textarea
          id="paste-inquiry-text"
          placeholder={"예) 이름: 홍길동\n연락처 010-1234-5678\n서울 성동구 ○○아파트 34평"}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <div className="flex items-center justify-between gap-x-2">
          <p className="text-xs text-muted-foreground">자동으로 저장되지 않습니다 — 확인한 뒤 아래 버튼으로 저장하세요.</p>
          <Button disabled={!text.trim()} size="sm" type="button" variant="outline" onClick={fill}>
            칸 채우기
          </Button>
        </div>
        {result && <PasteResultList phone={phone} result={result} onChoosePhone={(choice) => apply({ phone: choice })} />}
        {result && inputCut && <p className="text-xs text-muted-foreground">글이 너무 길어 앞부분에서만 칸을 찾았습니다.</p>}
      </div>
    </details>
  );
};

export default PasteInquiryPanel;
